import type { Transition, Variants } from 'framer-motion'

import type { ThemeId } from '../theme/ThemeContext'
import { useTheme } from '../theme/ThemeContext'

type EasingTuple = [number, number, number, number]

export type SpringPhysics = {
  damping: number
  stiffness: number
  mass: number
}

export type ThemeMotionPreset = {
  enterY: number
  enterScale: number
  /** 列表渐入单次时长（毫秒体感放慢一档） */
  duration: number
  stagger: number
  easing: EasingTuple
  /** Reveal/Stagger 使用弹簧位移而非 tween（cartoon） */
  useSpring?: boolean
  revealPhysics?: SpringPhysics
  /** 折叠面板高度动画时长（通常长于 reveal） */
  expandDuration: number
  /** 是否为近似匀速收尾 — pixel 等「数字感」 */
  expandLinear?: boolean
  /** 面板展开使用弹簧（更弹，仅少数主题） */
  expandUseSpring?: boolean
  /** 底栏/选项卡滑动指示条是否用弹簧 */
  tabSpring?: boolean
}

/** 系统勾选「减少动态效果」时使用：仅用透明度过渡，避免位移/缩放导致不适与多余布局开销 */
export function mergeReducedMotionPreset(
  preset: ThemeMotionPreset,
  reducedMotion: boolean,
): ThemeMotionPreset {
  if (!reducedMotion) return preset
  return {
    ...preset,
    enterY: 0,
    enterScale: 1,
    duration: 0.22,
    stagger: 0,
    useSpring: false,
    expandLinear: false,
    expandUseSpring: false,
    expandDuration: 0.2,
    tabSpring: false,
  }
}

const PHYS_CARTOON: SpringPhysics = { damping: 15, stiffness: 235, mass: 0.88 }
const PHYS_PIXEL: SpringPhysics = { damping: 32, stiffness: 420, mass: 0.55 }

const THEME_MOTION: Record<ThemeId, ThemeMotionPreset> = {
  warm: {
    enterY: 12,
    enterScale: 0.99,
    duration: 0.52,
    stagger: 0.11,
    easing: [0.22, 1, 0.36, 1],
    expandDuration: 0.54,
    expandLinear: false,
    tabSpring: false,
  },
  cool: {
    enterY: 16,
    enterScale: 0.987,
    duration: 0.56,
    stagger: 0.12,
    easing: [0.15, 0.85, 0.2, 1],
    expandDuration: 0.58,
    tabSpring: false,
  },
  dark: {
    enterY: 9,
    enterScale: 0.995,
    duration: 0.44,
    stagger: 0.095,
    easing: [0.25, 0.82, 0.25, 1],
    expandDuration: 0.48,
    tabSpring: false,
  },
  glass: {
    enterY: 14,
    enterScale: 0.986,
    duration: 0.52,
    stagger: 0.11,
    easing: [0.16, 1, 0.32, 1],
    expandDuration: 0.55,
    tabSpring: true,
  },
  cartoon: {
    enterY: 22,
    enterScale: 0.93,
    duration: 0.58,
    stagger: 0.13,
    easing: [0.15, 1.35, 0.18, 1],
    useSpring: true,
    revealPhysics: PHYS_CARTOON,
    expandDuration: 0.62,
    expandUseSpring: true,
    tabSpring: true,
  },
  illustration: {
    enterY: 14,
    enterScale: 0.988,
    duration: 0.53,
    stagger: 0.108,
    easing: [0.22, 0.92, 0.26, 1],
    expandDuration: 0.55,
    tabSpring: false,
  },
  pixel: {
    enterY: 5,
    enterScale: 1,
    duration: 0.36,
    stagger: 0.075,
    easing: [0.45, 0, 1, 1],
    useSpring: true,
    revealPhysics: PHYS_PIXEL,
    expandDuration: 0.34,
    expandLinear: true,
    expandUseSpring: true,
    tabSpring: false,
  },
  geek: {
    enterY: 10,
    enterScale: 0.993,
    duration: 0.43,
    stagger: 0.088,
    easing: [0.2, 0.92, 0.15, 1],
    expandDuration: 0.46,
    tabSpring: true,
  },
}

export function useThemeMotion(): ThemeMotionPreset {
  const { theme } = useTheme()
  return THEME_MOTION[theme]
}

export function resolveSpringReveal(preset: ThemeMotionPreset, delay = 0): Transition {
  const p =
    preset.revealPhysics ?? ({ damping: 18, stiffness: 280, mass: 0.75 } satisfies SpringPhysics)
  return {
    type: 'spring',
    damping: p.damping,
    stiffness: p.stiffness,
    mass: p.mass,
    delay,
  }
}

/** 渐入类（列表项、Reveal） */
export function resolveTransition(preset: ThemeMotionPreset, delay = 0): Transition {
  if (preset.useSpring && preset.duration >= 0.05) {
    return resolveSpringReveal(preset, delay)
  }
  return {
    duration: Math.max(preset.duration, 0.012),
    ease: [...preset.easing] as EasingTuple,
    delay,
  }
}

/**
 * 为非 Framer 节点生成与 {@link resolveExpandTransition} 对齐的 CSS transition 片段（例如侧栏占位 width）。
 */
export function cssTransitionFromExpandTransition(
  transition: Transition,
  physicalProperty: 'width' | 'opacity' | 'max-width',
): string {
  const durRaw = transition.duration
  const duration =
    typeof durRaw === 'number' && Number.isFinite(durRaw)
      ? durRaw
      : typeof durRaw === 'string' && /^[\d.]+/.test(durRaw)
        ? parseFloat(durRaw) / (/ms$/iu.test(String(durRaw)) ? 1000 : 1)
        : 0.54
  const ease = transition.ease
  let easeCss: string
  if (ease === 'linear') {
    easeCss = 'linear'
  } else if (typeof ease === 'string') {
    easeCss = ease
  } else if (Array.isArray(ease) && ease.length >= 4) {
    easeCss = `cubic-bezier(${ease.slice(0, 4).join(', ')})`
  } else {
    easeCss = 'ease'
  }
  return `${physicalProperty} ${duration}s ${easeCss}`
}

/** 折叠/展开面板、下拉等「布局」动效专用 */
export function resolveExpandTransition(
	preset: ThemeMotionPreset,
	opts?: { reducedMotion?: boolean },
): Transition {
  if (opts?.reducedMotion) {
    return { duration: Math.max(preset.expandDuration * 0.45, 0.16), ease: [0.4, 0, 1, 1] }
  }
  const d = preset.expandDuration
  // 高度/折叠禁止使用弹簧：height:auto 在弹簧下易过冲，结束回落时会出现「顶部或少了一截」的假像
  if (preset.expandLinear) {
    return {
      duration: Math.max(d, 0.02),
      ease: 'linear',
    }
  }
  return {
    duration: Math.max(d, 0.02),
    ease: [...preset.easing] as EasingTuple,
  }
}

/** 底部 / 移动端 选项卡「滑块指示条」 */
export function resolveTabIndicatorTransition(
  preset: ThemeMotionPreset,
  opts?: { reducedMotion?: boolean },
): Transition {
  if (opts?.reducedMotion) {
    return { duration: 0.14, ease: [0.4, 0, 1, 1] }
  }
  if (preset.tabSpring) {
    const bouncyTabs = !!(preset.useSpring && preset.expandUseSpring)
    return {
      type: 'spring',
      damping: bouncyTabs ? 22 : 28,
      stiffness: 400,
      mass: 0.72,
    }
  }
  return {
    duration: Math.min(0.45, Math.max(0.32, preset.duration * 0.72)),
    ease: [...preset.easing] as EasingTuple,
  }
}

export function buildRevealVariants(
  preset: ThemeMotionPreset,
  delay = 0,
  opts?: { reducedMotion?: boolean },
): Variants {
  const subtle = opts?.reducedMotion
  const p = subtle ? mergeReducedMotionPreset(preset, true) : preset
  return {
    hidden: {
      opacity: 0,
      y: subtle ? 0 : preset.enterY,
      scale: subtle ? 1 : preset.enterScale,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: resolveTransition(p, delay),
    },
  }
}

export type StaggerMotionOptions = {
  reducedMotion?: boolean
  /** 1 先子先播；-1 从 DOM 末尾子项起播（栅格偏右下先出） */
  staggerDirection?: 1 | -1
  /** 子项用 index×stagger 时置 0，避免与 staggerChildren 叠延迟 */
  staggerChildren?: number
  /** 仅一项时不先把容器 opacity 置 0，避免固定布局里先空一块再挤入 */
  skipContainerFade?: boolean
}

export function buildStaggerContainerVariants(
  preset: ThemeMotionPreset,
  delay = 0,
  opts?: StaggerMotionOptions,
): Variants {
  const subtle = opts?.reducedMotion
  const p = subtle ? mergeReducedMotionPreset(preset, true) : preset
  const staggerDirection = opts?.staggerDirection ?? 1
  const staggerChildren = opts?.staggerChildren ?? p.stagger
  const skipFade = opts?.skipContainerFade ?? false
  return {
    hidden: { opacity: subtle || skipFade ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: {
        ...resolveTransition(p, delay),
        staggerChildren,
        staggerDirection,
      },
    },
  }
}

export function buildStaggerItemVariants(
  preset: ThemeMotionPreset,
  opts?: StaggerMotionOptions,
): Variants {
  const subtle = opts?.reducedMotion
  const p = subtle ? mergeReducedMotionPreset(preset, true) : preset
  return {
    hidden: {
      opacity: subtle ? 1 : 0,
      y: subtle ? 0 : p.enterY,
      scale: subtle ? 1 : p.enterScale,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: resolveTransition(p),
    },
  }
}

/**
 * 分镜表等：裁切容器内自末端水平滑入（LTR 自右向左），第 n 项延迟 n×stagger。
 */
export function buildSlideInFromEndItemVariantsByIndex(
  preset: ThemeMotionPreset,
  opts?: StaggerMotionOptions,
): Variants {
  const subtle = opts?.reducedMotion
  const p = subtle ? mergeReducedMotionPreset(preset, true) : preset
  return {
    hidden: {
      opacity: subtle ? 1 : 0,
      x: subtle ? 0 : '100%',
    },
    visible: (index: number) => ({
      opacity: 1,
      x: 0,
      transition: resolveTransition(p, Math.max(0, index) * p.stagger),
    }),
  }
}

export function buildSlideInFromEndItemVariants(
  preset: ThemeMotionPreset,
  opts?: StaggerMotionOptions,
): Variants {
  const subtle = opts?.reducedMotion
  const p = subtle ? mergeReducedMotionPreset(preset, true) : preset
  return {
    hidden: {
      opacity: subtle ? 1 : 0,
      x: subtle ? 0 : '100%',
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: resolveTransition(p),
    },
  }
}
